import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styles from './styles.module.css'
import { Checkbox, Input, Select } from '../../../components/ModalInputs'
import {
  getLanguages,
  getShifts,
  getSpecializations,
  getStudyForms,
  getStudyModes,
  getStudyProfiles,
  getStudySteps
} from '../../../actions/common'
import { getSubgroupMain } from '../../../actions/directoryBasicEducation'

const PARALLELS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']

const OTHER_VALUE = '__other__'

// Селект с пунктом «Другое» — ручной ввод значения в той же строке
function ComboSelect({ label, name, value, options, setFieldValue }) {
  const [manual, setManual] = useState(false)
  const [draft, setDraft] = useState('')
  const [added, setAdded] = useState([])

  const handleSelect = (e) => {
    if (e.target.value === OTHER_VALUE) {
      setDraft('')
      setManual(true)
    } else {
      setFieldValue(name, e.target.value)
    }
  }

  const confirmDraft = () => {
    const val = draft.trim()
    if (!val) return
    setAdded(prev => prev.includes(val) ? prev : [...prev, val])
    setFieldValue(name, val)
    setManual(false)
  }

  if (manual) {
    return (
      <div className={styles.comboRow}>
        <Input
          label={label}
          value={draft}
          placeholder='Введите значение'
          autoFocus
          autoComplete='off'
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && confirmDraft()}
          style={{ width: '236px' }}
        />
        <button type='button' className={styles.comboOk} onClick={confirmDraft}>OK</button>
      </div>
    )
  }

  const allOptions = [...new Set([...(options || []), ...added, ...(value ? [value] : [])])]

  return (
    <Select
      label={label}
      name={name}
      value={value}
      onChange={handleSelect}
      style={{ width: '300px' }}
    >
      <option value=''>—</option>
      {allOptions.map(item => <option key={item} value={item}>{item}</option>)}
      <option value={OTHER_VALUE}>Другое (ввести вручную)…</option>
    </Select>
  )
}

export default function MainTab({
  values,
  handleChange,
  error,
  filters,
  electiveCourse,
  parallels,
  setParallels,
  gid,
  addMode,
  setFieldValue
}) {
  const dispatch = useDispatch()
  const { specializations, languages, studyProfiles, studySteps, shifts, studyForms, studyModes } = useSelector(state => state.common)
  const { subgroupMain } = useSelector(state => state.directoryBasicEducation)

  // Загрузка данных при первом открытии вкладки
  useEffect(() => {
    // Справочники загружаем всегда
    dispatch(getSpecializations())
    dispatch(getLanguages())
    dispatch(getStudyProfiles())
    dispatch(getStudyModes())
    dispatch(getStudyForms())
    dispatch(getStudySteps())
    dispatch(getShifts())

    // Для режима редактирования загружаем данные подгруппы
    if (gid && !addMode) {
      dispatch(getSubgroupMain(gid))
    }
  }, [gid, addMode])

  // Обновить значения формы при получении данных
  useEffect(() => {
    if (subgroupMain?.data && !addMode && setFieldValue) {
      const mainData = subgroupMain.data
      // Найти trp_id по имени профиля
      const profileId = studyProfiles?.find(p => p.name === mainData.track_profile_name)?.trp_id || ''
      setFieldValue('main', {
        name: mainData.name || '',
        shortname: mainData.shortname || '',
        specialization: mainData.specialization || '',
        language: mainData.language || '',
        study_mode: mainData.study_mode || '',
        shift: mainData.shift || '',
        track_profile_name: profileId,
        f_studyform: mainData.f_studyform || '',
        f_studysteps: mainData.f_studysteps || '',
      })
    }
  }, [subgroupMain, addMode, studyProfiles])

  const handleParallels = (e) => {
    if (parallels?.find(el => el === e.target.value))
      setParallels(parallels.filter(el => el !== e.target.value))
    else setParallels([...parallels, e.target.value])
  }

  const renderOptions = ({ name, xp_key, gid, f, title, cid, f_studysteps, f_studyform, trp_id, sid }) => (
    <option key={gid || xp_key || cid || f_studyform || f_studysteps || trp_id || sid} value={gid || xp_key || cid || f_studyform || f_studysteps || trp_id || sid}>
      {name || f || title}
    </option>
  )

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputsRow}>
        <Input
          label='Название'
          name='main.name'
          value={values?.main?.name}
          error={error('main.name')}
          onChange={handleChange}
          style={{ width: '600px' }}
        />
      </div>
      <div className={styles.inputsRow}>
        <Input
          label='Короткое название'
          name='main.shortname'
          value={values?.main?.shortname}
          onChange={handleChange}
          style={{ width: '600px' }}
        />
      </div>
      <div className={styles.inputsRow}>
        <div className={styles.inputsColumn}>
          <ComboSelect
            label='Специализация'
            name='main.specialization'
            value={values?.main?.specialization}
            options={specializations}
            setFieldValue={setFieldValue}
          />
          <ComboSelect
            label='Язык обучения'
            name='main.language'
            value={values?.main?.language}
            options={languages}
            setFieldValue={setFieldValue}
          />
          <ComboSelect
            label='Режим обучения'
            name='main.study_mode'
            value={values?.main?.study_mode}
            options={studyModes}
            setFieldValue={setFieldValue}
          />
          <Select
            label='Смена'
            name='main.shift'
            value={values?.main?.shift}
            onChange={handleChange}
            style={{ width: '300px' }}
          >
            <option value=''>{shifts?.group}</option>
            {shifts?.map(renderOptions)}
          </Select>
          <Select
            label='Профиль'
            name='main.track_profile_name'
            value={values?.main?.track_profile_name}
            onChange={handleChange}
            style={{ width: '300px' }}
          >
            <option value=''></option>
            {studyProfiles?.map(renderOptions)}
          </Select>
          <Select
            label='Форма обучения'
            name='main.f_studyform'
            value={values?.main?.f_studyform}
            onChange={handleChange}
            style={{ width: '300px' }}
          >
            <option value=''></option>
            {studyForms?.map(renderOptions)}
          </Select>
          <Select
            label='Ступень обучения'
            name='main.f_studysteps'
            value={values?.main?.f_studysteps}
            onChange={handleChange}
            style={{ width: '300px' }}
          >
            <option value=''></option>
            {studySteps?.map(renderOptions)}
          </Select>
        </div>
        <div className={styles.inputsColumn}>
          {electiveCourse && (
            <div className={styles.inputsColumnYears}>
              <p>Года обучения</p>
              {PARALLELS?.map(par => {
                const che = parallels?.find(el => +par == +el)
                return (
                  <Checkbox
                    key={par}
                    label={par}
                    checked={!!che}
                    value={par}
                    onChange={(e) => handleParallels(e)}
                    name='parallels'
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
